import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import ConstData from "../../lib/ConstData";

// import "./UserLoginAtCloud.css";

/**
 * 쿠키에 JSON 값을 저장
 * @param {string} name  - 쿠키 키
 * @param {any}    value - 저장할 값 (JSON 직렬화)
 * @param {number} days  - 만료일 (기본 365일)
 */
const setCookie = (name, value, days = 365) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/`;
};

/**
 * 쿠키에서 JSON 값을 읽기
 * @param {string} name - 쿠키 키
 * @returns {any|null}  - 파싱된 값, 없거나 파싱 실패 시 null
 */
const getCookie = (name) => {
    const match = document.cookie.match(
        new RegExp("(?:^|; )" + name.replace(/[\\^$*+?.()|[\]{}]/g, "\\$&") + "=([^;]*)")
    );
    try {
        return match ? JSON.parse(decodeURIComponent(match[1])) : null;
    } catch {
        return null;
    }
};

// 쿠키 키 상수 (hct_ 접두사 컨벤션 유지)
const COOKIE_KEY = {
    REMEMBER_ID_PW: "hct_cloud_remember", // 기억하기 활성 여부 (boolean)
    SAVED_USER_ID: "hct_cloud_user_id",  // 저장된 userID
    // ※ 보안상 password는 쿠키에 평문 저장하지 않음
    //   → userID만 저장하고, PW는 사용자가 직접 입력하도록 유도
};

// ─────────────────────────────────────────
// 커스텀 체크박스 컴포넌트
//
// CSS :checked + ::after 가상요소를 활용하므로
// input → span 순서가 반드시 형제 관계여야 함.
// (CSS 선택자: .hc-remember-check:checked + .hc-check-box)
//
// [Props]
//   t        : 부모에서 받은 useTranslation()의 번역 함수
//   checked  : 체크 여부
//   onChange : 체크 변경 핸들러
// ─────────────────────────────────────────
const CustomCheckbox = ({ checked, onChange, t }) => (
    <label className="hc-remember-label">
        {/* 실제 checkbox: CSS에서 display:none으로 숨김 */}
        <input
            type="checkbox"
            className="hc-remember-check"
            checked={checked}
            onChange={onChange}
        />
        {/* 커스텀 체크 박스 외형: checked 상태는 CSS로 처리 */}
        <span className="hc-check-box" />
        <span className="hc-remember-text">{t("ID_기억하기", { ns: "user" })}</span>
    </label>
);

// ─────────────────────────────────────────
// 메인 컴포넌트
// ─────────────────────────────────────────
const UserLoginAtCloud = ({
    handleSelectedFunc,
    loginMessage,
    alertKind,
    savedLocalServerSeq,
    checkAuthCodeResult,      // SettingLocalInfoModal과 동일: 인증 결과 객체
    saveCookiesAboutLocalInfo, // SettingLocalInfoModal과 동일: 쿠키 저장 콜백
}) => {
    // useTranslation: ns:"user" 네임스페이스에서 번역 키를 가져옴
    // 원본 UserLoginAtCloud.js와 동일한 방식 유지
    const { t } = useTranslation();

    // ── State ──
    // const [userID, setUserID] = useState("");
    // const [password, setPassword] = useState("");
    // const [rememberIdPw, setRememberIdPw] = useState(false); // ID/PW 기억 체크박스

    // ── State: 마운트 시 쿠키에서 초기값 로드 ──────────────────────
    // getCookie()는 마운트 1회만 실행되는 useState 초기화 함수 형태로 전달
    // (lazy initializer 패턴: () => ... 형태로 감싸면 렌더마다 실행 안 됨)
    const [rememberIdPw, setRememberIdPw] = useState(
        () => getCookie(COOKIE_KEY.REMEMBER_ID_PW) === true
    );
    const [userID, setUserID] = useState(
        // rememberIdPw가 true일 때만 저장된 userID 복원, 아니면 빈값
        () => getCookie(COOKIE_KEY.REMEMBER_ID_PW) === true
            ? (getCookie(COOKIE_KEY.SAVED_USER_ID) ?? "")
            : ""
    );
    const [password, setPassword] = useState("");

    const [errorMsg, setErrorMsg] = useState(loginMessage);
    const [errorKind, setErrorKind] = useState(alertKind);

    // 로컬 정보 설정 인라인 모달 열림 여부
    const [isModalOpen, setIsModalOpen] = useState(false);

    // ── SettingLocalInfoModal 로직 이식 ──
    // savedLocalServerSeq >= 0 이면 이미 인증된 상태 → 재설정 모드로 시작
    const [isSetUpLocalServerInfo, setIsSetUpLocalServerInfo] = useState(savedLocalServerSeq >= 0);
    const [isSettingMode, setIsSettingMode] = useState(savedLocalServerSeq < 0);
    const [lLocalServerAuthCode, setLocalServerAuthCode] = useState("");
    const [lAlertMessage, setAlertMessage] = useState("");
    const [lAlertKind, setAlertKind] = useState(ConstData.ALERT_KIND.NONE);

    // ── Ref ──
    // Toast DOM 직접 조작 (setState 없이 style 전환 → 리렌더 없음)
    const toastRef = useRef(null);
    const timerRef = useRef(null);   // setTimeout ID 보관
    // 토스트 위치 기준이 될 버튼 ref들
    const loginBtnRef = useRef(null);
    const settingBtnRef = useRef(null);
    // 모달 인증 입력창 auto-focus용
    const authInputRef = useRef(null);
    // 한글 조합 중 여부 (인증코드 입력 정제용)
    const isComposing = useRef(false);

    // ── 언마운트 시 쿠키 저장 (UserLoginAtLocal과 동일한 패턴) ──────
    // loginAttemptedRef: 로그인 버튼을 눌렀는지 여부 추적
    // → 로그인 성공 시 컴포넌트가 언마운트되므로, useEffect cleanup에서 저장
    const loginAttemptedRef = useRef(false);

    // state 최신값을 ref로 동기화 (cleanup 클로저에서 stale 값 방지)
    const rememberIdPwRef = useRef(rememberIdPw);
    const userIDRef = useRef(userID);

    // state가 바뀔 때마다 ref를 최신값으로 동기화
    useEffect(() => { rememberIdPwRef.current = rememberIdPw; }, [rememberIdPw]);
    useEffect(() => { userIDRef.current = userID; }, [userID]);

    // ── loginMessage / alertKind prop 변경 시 state 동기화 ──
    useEffect(() => { setErrorMsg(loginMessage); }, [loginMessage]);
    useEffect(() => { setErrorKind(alertKind); }, [alertKind]);

    // ─────────────────────────────────────────────────────────────
    // 언마운트 시 쿠키 저장
    //
    // 의존성 배열 []로 마운트 시 1회만 등록.
    // 클린업 내부에서 ref를 읽어 항상 최신 state를 참조한다.
    //
    // 흐름:
    //   handleLogin() → loginAttemptedRef = true
    //   → 로그인 성공 → 컴포넌트 언마운트
    //   → cleanup 실행: loginAttemptedRef = true → 쿠키 저장 ✅
    //
    //   C# 종료 → 컴포넌트 언마운트
    //   → cleanup 실행: loginAttemptedRef = false → 쿠키 저장 안함 ✅
    //
    //   로그인 실패 → 컴포넌트 언마운트 안됨 → cleanup 실행 안됨 ✅
    // ─────────────────────────────────────────────────────────────
    useEffect(() => {
        return () => {
            // 로그인 시도가 없었으면(외부 종료) 저장하지 않음
            if (!loginAttemptedRef.current) return;

            if (rememberIdPwRef.current) {
                // 기억하기 ON: 플래그 true + userID 저장
                setCookie(COOKIE_KEY.REMEMBER_ID_PW, true);
                setCookie(COOKIE_KEY.SAVED_USER_ID, userIDRef.current);
            } else {
                // 기억하기 OFF: 기존 저장값 삭제 (만료일을 과거로 → 즉시 삭제)
                setCookie(COOKIE_KEY.REMEMBER_ID_PW, false, -1);
                setCookie(COOKIE_KEY.SAVED_USER_ID, "", -1);
            }
        };
    }, []); // 마운트 시 한 번만 cleanup 등록

    // ─────────────────────────────────────────────────────────────
    // checkAuthCodeResult 변경 감지 → 인증 성공/실패 상태 반영
    // ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (checkAuthCodeResult.isCorrectAuthCode && checkAuthCodeResult.localServerSeq >= 0 && checkAuthCodeResult.localServerName && checkAuthCodeResult.localServerName !== "") {
            // 인증 성공: [확인] 버튼을 표시하는 상태로 전환
            setIsSetUpLocalServerInfo(true);
            // setIsSettingMode(false);
        } else {
            // 인증 실패 or 초기 상태: [인증] 버튼 유지
            setIsSetUpLocalServerInfo(false);
            // setIsSettingMode(true);
        }
    }, [savedLocalServerSeq, checkAuthCodeResult.isCorrectAuthCode, checkAuthCodeResult.localServerSeq, checkAuthCodeResult.localServerName]);

    useEffect(() => {
        setAlertKind(checkAuthCodeResult.alertKind);
    }, [checkAuthCodeResult.alertKind]);

    useEffect(() => {
        setAlertMessage(checkAuthCodeResult.alertMessage);
    }, [checkAuthCodeResult.alertMessage]);

    // ── 모달이 열릴 때 인증 입력창에 자동 포커스 ──
    useEffect(() => {
        if (isModalOpen) {
            const id = setTimeout(() => authInputRef.current?.focus(), 100);
            return () => clearTimeout(id);
        }
    }, [isModalOpen]);

    // ─────────────────────────────────────
    // 아이콘 / 가이드 텍스트 활성화 여부
    // ID, PW 모두 입력되면 .hc-active 클래스 적용 → 파란색 전환
    // ─────────────────────────────────────
    const isBothFilled = userID.trim() !== "" && password.trim() !== "";

    // ─────────────────────────────────────
    // 공용 토스트(말풍선) 표시 함수
    //
    // targetRef : 토스트를 띄울 기준 버튼의 ref
    // msg       : 표시할 메시지
    //
    // DOM을 직접 조작하는 이유:
    //   setState로 show/hide를 관리하면 토스트가 나타날 때마다
    //   컴포넌트 전체가 리렌더됨. ref.current.style 직접 변경으로 방지.
    // ─────────────────────────────────────
    const showToast = useCallback((msg, targetRef) => {
        const el = toastRef.current;
        const btn = targetRef?.current;
        if (!el || !btn) return;

        const rect = btn.getBoundingClientRect();

        // 버튼 가운데 아래에 위치
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${window.scrollY + rect.bottom + 8}px`;
        el.textContent = msg;

        // 보임 상태로 전환
        el.style.opacity = "1";
        el.style.transform = "translateX(-50%) translateY(0)";

        // 기존 타이머 초기화 후 2초 뒤 숨김
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (!toastRef.current) return;
            toastRef.current.style.opacity = "0";
            toastRef.current.style.transform = "translateX(-50%) translateY(6px)";
        }, 2000);
    }, []);

    // ─────────────────────────────────────
    // 로그인 처리
    // 원본과 동일: userID → password 순서로 유효성 검사 후
    // handleSelectedFunc({ selectedFunc: "LOGIN", ... }) 호출
    // ─────────────────────────────────────
    const handleLogin = useCallback(() => {
        if (userID === "") {
            // 다국어: "유효성_검사_ID_입력오류" (ns: user)
            handleSelectedFunc({
                selectedFunc: "L_MESSAGE",
                message: t("유효성_검사_ID_입력오류", { ns: "user" }),
                kind: "danger",
            });
            showToast(t("유효성_검사_ID_입력오류", { ns: "user" }), loginBtnRef);
            return;
        }
        if (password === "") {
            // 다국어: "유효성_검사_비밀번호_입력오류" (ns: user)
            handleSelectedFunc({
                selectedFunc: "L_MESSAGE",
                message: t("유효성_검사_비밀번호_입력오류", { ns: "user" }),
                kind: "danger",
            });
            showToast(t("유효성_검사_비밀번호_입력오류", { ns: "user" }), loginBtnRef);
            return;
        }

        // 유효 → 에러 메시지 초기화 후 로그인 액션 dispatch
        handleSelectedFunc({ selectedFunc: "L_MESSAGE", message: "", kind: "none" });

        // ★ 로그인 시도 플래그 세팅 → 언마운트 cleanup에서 쿠키 저장 허용
        loginAttemptedRef.current = true;

        handleSelectedFunc({
            selectedFunc: "LOGIN",
            loginInfo: { userID, password, isAutoLogin: false, rememberIdPw },
            mode: "hiclass",
        });
    }, [userID, password, rememberIdPw, handleSelectedFunc, showToast, t]);

    // Enter 키 로그인 지원 (원본: onKeyPress → onKeyDown으로 대체)
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter") handleLogin();
    }, [handleLogin]);

    // ─────────────────────────────────────
    // 입력창 onChange 핸들러
    // 입력 시작 즉시 에러 메시지 초기화
    // ─────────────────────────────────────
    const onChangeUserId = useCallback((e) => {
        setUserID(e.target.value);
        handleSelectedFunc({ selectedFunc: "L_MESSAGE", message: "", kind: "none" });
    }, [handleSelectedFunc]);

    const onChangePassword = useCallback((e) => {
        setPassword(e.target.value);
        handleSelectedFunc({ selectedFunc: "L_MESSAGE", message: "", kind: "none" });
    }, [handleSelectedFunc]);

    // ─────────────────────────────────────
    // 로컬 정보 설정 모달 제어
    // ─────────────────────────────────────
    const openModal = () => {
        // 모달을 열 때마다 savedLocalServerSeq(쿠키값)를 기준으로 state 재초기화.
        // 인증 완료 후 모달을 닫았다가 다시 열면 쿠키에 localServerSeq가 저장된 상태이므로
        // isSettingMode=false(재설정 화면)로 진입해야 한다.
        // useState 초기값은 마운트 시 1회만 평가되므로, 여기서 직접 재계산해야 함.
        console.log("openModal - savedLocalServerSeq[", savedLocalServerSeq, "]");
        setIsSettingMode(savedLocalServerSeq < 0);
        setIsSetUpLocalServerInfo(savedLocalServerSeq >= 0);
        setLocalServerAuthCode("");
        setAlertKind(ConstData.ALERT_KIND.NONE);
        setAlertMessage("");
        setIsModalOpen(true);
    };

    const closeModal = () => {
        // 모달 닫을 때도 동일하게 쿠키 기준으로 state 복원 + 입력값/알림 초기화
        console.log("closeModal - savedLocalServerSeq[", savedLocalServerSeq, "]");
        setIsSettingMode(savedLocalServerSeq < 0);
        setIsSetUpLocalServerInfo(savedLocalServerSeq >= 0);
        setLocalServerAuthCode("");
        setAlertKind(ConstData.ALERT_KIND.NONE);
        setAlertMessage("");
        setIsModalOpen(false);
    };

    // ─────────────────────────────────────
    // Local로 이동 버튼
    // savedLocalServerSeq >= 0 일 때만 유효
    // ─────────────────────────────────────
    const handleGoToLocal = () => {
        handleSelectedFunc({ selectedFunc: "GO_TO_LOCAL" });
    };

    // ─────────────────────────────────────
    // 로컬 정보 설정 버튼
    // Redux SettingLocalInfoModal 대신 hc-modal-card(인라인 모달)를 직접 열고
    // SETTING_LOCAL_INFO_TOGGLE은 호출하지 않음 → 모달 중복 방지
    // ─────────────────────────────────────
    const handleOpenSettingModal = () => {
        openModal();
    };

    // ─────────────────────────────────────
    // 에러 배너 렌더 헬퍼
    // alertKind에 따라 .hc-alert-danger / .hc-alert-success 적용
    // ─────────────────────────────────────
    const renderAlert = () => {
        if (!errorMsg) return null;

        const kindClass = errorKind === ConstData.ALERT_KIND.DANGER
            ? "hc-alert-danger"
            : "hc-alert-success";

        return (
            <div
                className={`hc-alert ${kindClass}`}
                style={{ textAlign: 'center' }}
                dangerouslySetInnerHTML={{ __html: errorMsg }}
            />
        );
    };

    /**
     * 입력값이 변경될 때 항상 이 함수를 통해 상태를 업데이트하는 헬퍼 함수.
     *   → 입력값이 바뀌는 모든 경로(삽입, 삭제, onChange, onCompositionEnd)를
     *     이 함수로 통합하여 항상 alert를 초기화한다.
     * 
     * @param {String} newValue 
     * @since 2026-05-15.
     */
    const applyAuthCodeChange = (newValue) => {
        // ─────────────────────────────────────────────────────────────
        // alert가 떠있는 상태라면 입력 변경 즉시 초기화
        // 이렇게 하면 이전 인증 요청의 결과 메시지와 새 입력이 섞이지 않음
        // ─────────────────────────────────────────────────────────────
        if (lAlertKind !== ConstData.ALERT_KIND.NONE || lAlertMessage !== "") {
            setAlertKind(ConstData.ALERT_KIND.NONE);
            setAlertMessage("");
        }

        setLocalServerAuthCode(newValue);
    }

    /**
     * [인증] 버튼 클릭: 입력값 유효성 체크 후 인증 요청
     */
    const checkAuthCodeInputValue = () => {
        if (lLocalServerAuthCode === undefined || lLocalServerAuthCode === null || lLocalServerAuthCode === "") {
            setAlertKind(ConstData.ALERT_KIND.DANGER);
            setAlertMessage(t("로컬_인증코드_입력", { ns: "user" }));
        } else {
            handleSelectedFunc({ selectedFunc: "REQUEST_AUTH_CODE_CHECK", value: lLocalServerAuthCode });
        }
    }

    /**
     * [확인] 버튼 클릭: 인증된 로컬 서버 정보 저장
     */
    const checkLocalServerInfoValue = () => {
        console.log(`checkLocalServerInfoValue - localServerSeq[${checkAuthCodeResult.localServerSeq}]`);
        if (checkAuthCodeResult.localServerSeq >= 0) {
            saveCookiesAboutLocalInfo({ kind: "localServerSeq", value: checkAuthCodeResult.localServerSeq });
            closeModal();
        } else {
            closeModal();
        }
    }

    /**
     * [재설정] 버튼 클릭: 다시 설정 모드(input 입력 화면)로 전환
     */
    const resetLocalServerInfo = () => {
        setIsSettingMode(true);
        setIsSetUpLocalServerInfo(false);
    }

    /**
     * 영숫자만 허용하고 대문자로 변환하는 정규화 함수.
     * 한글, 특수문자 등 모두 제거
     * 
     * @param {String} str 변환할 문자열
     * @returns 변환된 문자열
     * @since 2026-02-03.
     */
    const normalize = (str) => str.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    /**
     * 커서 위치를 기준으로 텍스트를 삽입하는 함수.
     * onBeforeInput 이벤트에서 preventDefault 후 직접 호출됨.
     * → alert 초기화 및 isSetUpLocalServerInfo 리셋은 applyAuthCodeChange에서 처리
     * 
     * @param {String} text 
     * @since 2026-02-03. (@modify 2026-05-15.)
     */
    const insertTextAtCursor = (text) => {
        const el = authInputRef.current;
        if (!el) return;

        const cur = lLocalServerAuthCode ?? "";
        const s = el.selectionStart ?? cur.length;
        const e = el.selectionEnd ?? cur.length;

        // 선택 영역(s~e)을 삽입 텍스트로 교체
        const next = cur.slice(0, s) + text + cur.slice(e);

        // ▶ [20260515 수정]: setLocalServerAuthCode 직접 호출 대신 applyAuthCodeChange로 통합
        //   → [버그 1] alert 초기화, [버그 2] isSetUpLocalServerInfo 리셋 처리
        // setLocalServerAuthCode(next);
        applyAuthCodeChange(next);

        // 커서 위치 복구
        // 삽입 후 커서를 삽입된 텍스트 끝으로 이동
        requestAnimationFrame(() => {
            const pos = s + text.length;
            el.setSelectionRange(pos, pos);
        });
    }

    /**
     * 커서 위치 기준으로 문자를 삭제하는 함수.
     * onBeforeInput의 delete 계열 inputType은 기본 동작에 맡기므로
     * 이 함수는 현재 직접 호출되지 않지만, 향후 직접 제어가 필요할 때를 위해 유지.
     * 
     * @param {String} type 
     * @since 2026-02-03. (@modify 2026-05-15.)
     */
    const applyDelete = (type) => {
        const el = authInputRef.current;
        if (!el) return;

        const cur = lLocalServerAuthCode ?? "";
        let s = el.selectionStart ?? cur.length;
        let e = el.selectionEnd ?? cur.length;

        let next;

        // 선택 영역이 있으면 선택된 범위 전체 삭제
        if (s !== e) {
            next = cur.slice(0, s) + cur.slice(e);
            // ▶ 수정: applyAuthCodeChange로 통합
            applyAuthCodeChange(next);
            requestAnimationFrame(() => el.setSelectionRange(s, s));
            return;
        }

        // 커서 기준 삭제: Backspace(deleteContentBackward) / Delete(deleteContentForward)
        if (type === "deleteContentBackward" && s > 0) {
            next = cur.slice(0, s - 1) + cur.slice(s);
            // ▶ 수정: applyAuthCodeChange로 통합
            applyAuthCodeChange(next);
            requestAnimationFrame(() => el.setSelectionRange(s - 1, s - 1));
        } else if (type === "deleteContentForward" && s < cur.length) {
            next = cur.slice(0, s) + cur.slice(s + 1);
            // ▶ 수정: applyAuthCodeChange로 통합
            applyAuthCodeChange(next);
            requestAnimationFrame(() => el.setSelectionRange(s, s));
        }
    }

    /**
     * onBeforeInput 이벤트 핸들러.
     * 실제 DOM에 값이 반영되기 전에 가로채서 우리가 직접 값을 제어.
     * 
     * ▶ delete 계열은 preventDefault하지 않고 브라우저 기본 동작에 맡김.
     *   (기본 동작 후 onChange가 발생하고, 거기서 applyAuthCodeChange 호출됨)
     * ▶ 일반 문자 입력은 normalize()로 영숫자 대문자만 허용 후 insertTextAtCursor로 삽입.
     * 
     * @param {Event} e - 입력 이벤트
     * @since 2026-02-03. (@modify 2026-05-15.)
     */
    const onBeforeInputLocalServerAuthCode = (e) => {
        const inputType = e.inputType ?? e.nativeEvent?.inputType;
        const data = e.data ?? e.nativeEvent?.data;

        // delete 계열은 브라우저 기본 동작 허용 (브라우저 기본 동작 쓰는 게 제일 안전, preventDefault 하지 않음)
        // → 이후 onChange가 발생하고, 거기서 applyAuthCodeChange가 호출됨
        if (inputType?.startsWith("delete")) return;

        // data가 없는 경우(일부 브라우저/붙여넣기 등 일부 케이스)는 onChange fallback에 맡김
        if (typeof data !== "string") return;

        const filtered = normalize(data);

        // 영숫자가 전혀 없는 입력(한글만, 특수문자만 등)은 입력 자체를 완전 차단
        if (!filtered) {
            e.preventDefault();
            return;
        }

        // ✅ 여기 중요: 대문자/영숫자 보장: 우리가 직접 삽입 (소문자든 뭐든)
        // insertTextAtCursor → applyAuthCodeChange 순으로 호출되므로
        // alert 초기화, isSetUpLocalServerInfo 리셋이 여기서도 적용됨
        e.preventDefault();
        insertTextAtCursor(filtered);
    }

    /**
     * onChange 이벤트 핸들러.
     * 
     * 주로 아래 두 경우에 호출됨:
     *   1) onBeforeInput에서 delete를 허용한 후 브라우저가 값을 변경했을 때
     *   2) onBeforeInput으로 처리되지 않은 예외 케이스(붙여넣기 등)
     * 
     * 조합(composition) 중에는 onCompositionEnd에서 처리하므로 여기선 건너뜀
     * 
     * @param {Event} e - 입력 이벤트
     */
    const onChangeLocalServerAuthCode = (e) => {
        /* if (lAlertKind !== ConstData.ALERT_KIND.NONE || lAlertMessage !== "") {
            setAlertKind(ConstData.ALERT_KIND.NONE);
            setAlertMessage("");
        }

        let value = e.target.value;
        if (isComposing.current) { // 조합 중엔 그대로 반영 (절대 가공 X)
            console.log("(1) onChangeLocalServerAuthCode - value[" + value + "]");
            setLocalServerAuthCode(value);
        } else {
            value = normalize(value);
            console.log("(2) onChangeLocalServerAuthCode - value[" + value + "]");
            setLocalServerAuthCode(value);
        } */

        /* // beforeinput에서 대부분 preventDefault로 처리되므로
        // 여기로 들어오는 값은 "예외 케이스"로 보고 normalize만 해도 됨
        if (isComposing.current) return;
        setLocalServerAuthCode(normalize(e.target.value)); */

        // 조합 중에는 onCompositionEnd에서 처리
        if (isComposing.current) return;

        const normalized = normalize(e.target.value);

        // ▶ [20260515 수정]: setLocalServerAuthCode 직접 호출 대신 applyAuthCodeChange로 통합
        //   → alert 초기화, isSetUpLocalServerInfo 리셋 처리
        //   delete 키로 글자를 지워도 이 경로를 통해 상태가 갱신되므로
        //   인증 성공 후 글자를 지워도 [확인] 버튼이 [인증]으로 되돌아간다.
        applyAuthCodeChange(normalized);
    }

    /**
     * IME 조합 시작 (한글 등 조합 문자 입력 시작)
     * 
     * @param {Event} e - 입력 이벤트
     * @since 2026-02-03. (@modify 2026-05-15.)
     */
    const onComposingStartLocalServerAuthCode = (e) => {
        isComposing.current = true;
    }

    /**
     * IME 조합 완료 (한글 등 조합 완료 시점)
     * 
     * 조합 완료 후 최종 값을 normalize()로 정리.
     * (한글 조합 중에 들어온 값이 완료 시점에 영숫자만 남도록 처리)
     * 
     * @param {Event} e - 입력 이벤트
     * @since 2026-02-03. (@modify 2026-05-15.)
     */
    const onComposingEndLocalServerAuthCode = (e) => {
        /* isComposing.current = false;

        // 조합 완료된 값에서 최종 정리(한글 제거 + 대문자)
        setLocalServerAuthCode(normalize(e.target.value)); */

        isComposing.current = false;

        // 조합 완료 후 한글/특수문자 제거, 대문자 정규화
        const normalized = normalize(e.target.value);

        // ▶ [20260515 수정]: setLocalServerAuthCode 직접 호출 대신 applyAuthCodeChange로 통합
        //   → alert 초기화, isSetUpLocalServerInfo 리셋 처리
        applyAuthCodeChange(normalized);
    }

    /**
     * 모달 닫힘 완료 시 상태 초기화.
     */
    const onClosedModal = () => {
        setAlertKind(ConstData.ALERT_KIND.NONE);
        setAlertMessage("");
        setLocalServerAuthCode(null);
    }

    // ═══════════════════════════════════════
    // JSX
    // ═══════════════════════════════════════
    return (
        <div className="hc-wrapper">

            {/* ────────────── 로그인 카드 ────────────── */}
            <div className="hc-card">

                {/* 카드 상단 여백 */}
                <div style={{ marginBottom: 20 }} />

                {/* ── 상단 아이콘 + 가이드 텍스트 ── */}
                <div className="hc-step-fixed-header">

                    {/* 좌측 여백 (뒤로가기 버튼 자리 예약) */}
                    <div className="hc-back-btn-space" />

                    {/* 중앙 배지: 아이콘 + 가이드 텍스트 */}
                    <div className="hc-selected-badge">
                        {/*
                            유저 아이콘 SVG
                            isBothFilled true  → .hc-badge-svg.hc-active (파란색)
                            isBothFilled false → .hc-badge-svg (회색)
                        */}
                        <svg
                            className={`hc-badge-svg${isBothFilled ? " hc-active" : ""}`}
                            xmlns="http://www.w3.org/2000/svg"
                            width="24" height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M17.925 20.056a6 6 0 0 0-11.851.001" />
                            <circle cx="12" cy="11" r="4" />
                            <circle cx="12" cy="12" r="10" />
                        </svg>

                        <span className={`hc-guide-text${isBothFilled ? " hc-active" : ""}`}>
                            {t("yourID_PW_입력_안내", { ns: "user" })}
                        </span>
                    </div>

                    {/* 우측 여백 */}
                    <div className="hc-back-btn-space" />
                </div>

                {/* ── 폼 영역 ── */}
                <div className="hc-form-body">

                    {/* 입력창 그룹 */}
                    <div className="hc-input-group">
                        {/*
                            placeholder: 다국어 "yourID" (ns: user)
                            원본: t("yourID", { ns: "user" })
                        */}
                        <input
                            type="text"
                            className="hc-custom-input"
                            placeholder={t("yourID", { ns: "user" })}
                            value={userID}
                            onChange={onChangeUserId}
                            onKeyDown={handleKeyDown}
                            autoComplete="username"
                        />
                        {/*
                            placeholder: 다국어 "yourPW" (ns: user)
                            원본: t("yourPW", { ns: "user" })
                        */}
                        <input
                            type="password"
                            className="hc-custom-input"
                            placeholder={t("yourPW", { ns: "user" })}
                            value={password}
                            onChange={onChangePassword}
                            onKeyDown={handleKeyDown}
                            autoComplete="current-password"
                        />
                    </div>

                    {/* 에러/성공 메시지 배너 */}
                    {renderAlert()}

                    {/*
                        로그인 버튼
                        다국어: "Login" (ns: user)
                        원본: {t("Login", { ns: "user" })}
                    */}
                    <button
                        ref={loginBtnRef}
                        className="hc-login-btn"
                        onClick={handleLogin}
                    >
                        {t("LOGIN", { ns: "user" })}
                    </button>

                    {/*
                        ID, PW 기억하기 체크박스
                        t를 prop으로 전달해 CustomCheckbox 내부에서 다국어 처리
                    */}
                    <CustomCheckbox
                        checked={rememberIdPw}
                        onChange={e => setRememberIdPw(e.target.checked)}
                        t={t}
                    />

                    {/* 하단 부가 기능 버튼 */}
                    <div className="hc-action-btns-wrapper">

                        {/*
                            Local로 이동 버튼
                            다국어: "로컬로_이동" (ns: user)
                            원본: {t("로컬로_이동", { ns: "user" })}
                            savedLocalServerSeq === -1: 로컬 서버 미설정
                            → disabled + .hc-disabled (opacity 0.4, cursor not-allowed)
                        */}
                        <button
                            type="button"
                            className={`hc-local-btn${savedLocalServerSeq === -1 ? " hc-disabled" : ""}`}
                            onClick={handleGoToLocal}
                            disabled={savedLocalServerSeq === -1}
                        >
                            {/* 모니터 아이콘 */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                <line x1="8" y1="21" x2="16" y2="21" />
                                <line x1="12" y1="17" x2="12" y2="21" />
                            </svg>
                            {t("로컬로_이동", { ns: "user" })}
                        </button>

                        {/*
                            로컬 정보 설정 버튼
                            다국어: "로컬_정보_설정" (ns: user)
                            원본: {t("로컬_정보_설정", { ns: "user" })}
                        */}
                        <button
                            ref={settingBtnRef}
                            type="button"
                            className="hc-setting-btn"
                            onClick={handleOpenSettingModal}
                        >
                            {/* 설정(기어) 아이콘 */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="3" />
                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83
                                    2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33
                                    1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09
                                    A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06
                                    a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06
                                    a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3
                                    a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9
                                    a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83
                                    2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9
                                    a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09
                                    a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06
                                    a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06
                                    a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21
                                    a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                            </svg>
                            {t("로컬_정보_설정", { ns: "user" })}
                        </button>
                    </div>
                </div>

                {/*
                    Forgot 링크
                    다국어: "Forgot" / "ID/Password" (ns: user)
                    원본: {t("Forgot", { ns: "user" })} / {t("ID/Password", { ns: "user" })}
                */}
                {/* 260804 휴대폰 번호 인증 시스템 제거로 ID/Password 찾기(휴대폰 인증 기반) 진입점 비활성화 by claude
                <div className="hc-signup-link">
                    {t("Forgot", { ns: "user" })}{" "}
                    <a
                        href="#find"
                        onClick={(e) => {
                            e.preventDefault();
                            handleSelectedFunc({ selectedFunc: "FIND_TOGGLE", mode: "web" });
                        }}
                    >
                        <u>{t("ID/Password", { ns: "user" })}</u>
                    </a>
                </div>
                */}
            </div>

            {/* ────────────── 토스트(말풍선) ────────────── */}
            {/*
                CSS: .hc-toast (기본 숨김)
                showToast()에서 ref.style 직접 조작으로 표시/숨김 전환.
                ::after / ::before 꼭짓점은 CSS에서만 처리 가능 (인라인 불가).
            */}
            <div
                ref={toastRef}
                className="hc-toast"
            />

            {/* ────────────── 로컬 정보 설정 인라인 모달 ────────────── */}
            {/*
                SettingLocalInfoModal의 3단계 분기를 그대로 이식:
                [isSettingMode=true  + isSetUpLocalServerInfo=false] → 인증코드 입력 + [인증] 버튼
                [isSettingMode=true  + isSetUpLocalServerInfo=true ] → 인증코드 입력 + [확인] 버튼
                [isSettingMode=false                               ] → 기존 설정 안내 + [재설정] 버튼
            */}
            <div
                className={`hc-modal-overlay${isModalOpen ? " hc-show" : ""}`}
            >
                <div className="hc-modal-card">
                    <div className="hc-modal-title">{t("로컬_정보_설정", { ns: "user" })}</div>

                    {/* ── 본문: isSettingMode에 따라 입력창 or 안내 텍스트 ── */}
                    {isSettingMode ? (
                        // 인증코드 입력 영역
                        <div>
                            <label className="hc-modal-label" htmlFor="hc-authCode">
                                {t("로컬_서버_인증코드", { ns: "user" })}
                            </label>
                            <input
                                ref={authInputRef}
                                id="hc-authCode"
                                type="text"
                                className="hc-custom-input"
                                placeholder={
                                    lLocalServerAuthCode && lLocalServerAuthCode !== ""
                                        ? lLocalServerAuthCode
                                        : t("로컬_인증코드_입력_안내", { ns: "user" })
                                }
                                value={lLocalServerAuthCode ?? ""}
                                onBeforeInput={onBeforeInputLocalServerAuthCode}
                                onChange={onChangeLocalServerAuthCode}
                                onCompositionStart={onComposingStartLocalServerAuthCode}
                                onCompositionEnd={onComposingEndLocalServerAuthCode}
                                onKeyDown={e => e.key === "Enter" && checkAuthCodeInputValue()}
                                autoComplete="off"
                            />
                        </div>
                    ) : (
                        // 기존 설정 완료 안내 텍스트
                        <div className="hc-modal-info-text" style={{ color: '#f0f0f0', fontSize: '.8rem', wordWrap: 'break-word', wordBreak: 'keep-all', textAlign: 'center' }}>
                            <p>{t("로컬_기존_설정_정보_안내", { ns: "user" })}</p>
                            <p>{t("로컬_서버_재설정_안내", { ns: "user" })}</p>
                        </div>
                    )}

                    {/* 알림 메시지 (에러/성공) */}
                    {lAlertMessage ? (
                        <div className={`hc-alert ${lAlertKind === ConstData.ALERT_KIND.DANGER ? "hc-alert-danger" : "hc-alert-success"}`} style={{ textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: lAlertMessage }}>
                        </div>
                    ) : null}

                    {/* ── 버튼: isSettingMode + isSetUpLocalServerInfo 조합으로 3가지 분기 ── */}
                    <div className="hc-modal-btns">
                        {isSettingMode ? (
                            isSetUpLocalServerInfo ? (
                                // 인증 완료 → [확인] 버튼으로 쿠키 저장
                                <button
                                    type="button"
                                    className="hc-login-btn"
                                    onClick={checkLocalServerInfoValue}
                                >
                                    {t("확인", { ns: "common" })}
                                </button>
                            ) : (
                                // 인증 전 → [인증] 버튼
                                    <>
                                        <button
                                            type="button"
                                            className="hc-login-btn"
                                            onClick={checkAuthCodeInputValue}
                                        >
                                            {t("인증", { ns: "user" })}
                                        </button>
                                        {/* 닫기 버튼 */}
                                        <button
                                            type="button"
                                            className="hc-modal-cancel-btn"
                                            onClick={closeModal}
                                        >
                                            {t("닫기", { ns: "common" })}
                                        </button>
                                    </>
                            )
                        ) : (
                            // 기존 설정 있음 → [재설정] 버튼
                            <>
                                <button
                                    type="button"
                                    className="hc-login-btn"
                                    onClick={resetLocalServerInfo}
                                >
                                    {t("재설정", { ns: "user" })}
                                </button>
                                {/* 닫기 버튼 */}
                                <button
                                    type="button"
                                    className="hc-modal-cancel-btn"
                                    onClick={closeModal}
                                >
                                    {t("닫기", { ns: "common" })}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default UserLoginAtCloud;